import React from 'react';
import { onAppPaneGroupReady } from 'owa-app-pane-group-actions';
import getDrawerPaneId from 'owa-app-pane-group-utils/lib/getDrawerPaneId';
import { DrawerPane, type DrawerPaneProps } from 'owa-drawer';
import loc from 'owa-localize';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';
import { observer } from 'owa-mobx-react';
import { useAction } from 'owa-react-hooks/lib/useAction';
import { editorPaneHeader, helpPaneHeader, tipsPaneHeader } from './AppPaneGroup.locstring.json';
import {
    addinsHeader,
    addinsBody,
    addinsTitle,
    rolloutOverridesBody,
    tipsBody,
} from './AppPaneGroup.scss';

import {
    AddinsFlexPaneBody as LazyAddinsFlexPaneBody,
    AddinsFlexPaneHeader as LazyAddinsFlexPaneHeader,
} from 'owa-addins-view';
import { DictationHelpPane as LazyDictationHelpPane } from 'owa-editor-dictation';
import { DexPaneWrapper as LazyDexPaneWrapper } from 'owa-editor-proofing-dex-pane';
import { RolloutOverridesPane as LazyRolloutOverridesPane } from 'owa-header-pane';
import { WhatsNewFluentPane as LazyWhatsNewFluentPane } from 'owa-whats-new';
import closeAppPane from '../orchestration/closeAppPane';

import ChatCopilotPane from './panes/ChatCopilotPane';
import FeedbackPane from './panes/FeedbackPane';
import GetDiagnostics from './panes/GetDiagnostics';
import SupportPane from './panes/SupportPane';

export default observer(function AppPaneGroup() {
    useAction(onAppPaneGroupReady);

    const mailboxInfo = getModuleContextMailboxInfo();

    const handleDismiss = React.useCallback(
        (paneId: string) => () => {
            closeAppPane(paneId);
        },
        []
    );

    const drawerPanes: ReadonlyArray<DrawerPaneProps> = React.useMemo(
        () => [
            {
                paneId: getDrawerPaneId('editor'),
                headerText: loc(editorPaneHeader),
                children: <LazyDexPaneWrapper />,
            },
            {
                paneId: getDrawerPaneId('help'),
                headerText: loc(helpPaneHeader),
                children: <>The help section is currently being developed. 🚧</>,
            },
            {
                paneId: getDrawerPaneId('rolloutOverrides'),
                headerText: 'Rollout Overrides',
                children: <LazyRolloutOverridesPane />,
                bodyClassName: rolloutOverridesBody,
            },
            {
                paneId: getDrawerPaneId('tips'),
                headerText: loc(tipsPaneHeader),
                children: <LazyWhatsNewFluentPane />,
                bodyClassName: tipsBody,
            },
            {
                paneId: getDrawerPaneId('dictationHelp'),
                headerText: loc(helpPaneHeader),
                children: <LazyDictationHelpPane />,
            },
            {
                paneId: getDrawerPaneId('addins'),
                headerText: (
                    <LazyAddinsFlexPaneHeader
                        mailboxInfo={mailboxInfo}
                        renderTaskPaneInPanel={true}
                    />
                ),
                children: <LazyAddinsFlexPaneBody mailboxInfo={mailboxInfo} />,
                showCloseButton: false,
                headerClassName: addinsHeader,
                titleClassName: addinsTitle,
                bodyClassName: addinsBody,
            },
        ],
        [mailboxInfo]
    );

    return (
        <>
            <FeedbackPane />
            <GetDiagnostics />
            <SupportPane />
            <ChatCopilotPane />
            {drawerPanes.map(
                ({
                    paneId,
                    headerText,
                    children,
                    showCloseButton,
                    headerClassName,
                    titleClassName,
                    bodyClassName,
                }) => (
                    <DrawerPane
                        key={paneId}
                        paneId={paneId}
                        headerText={headerText}
                        onDismiss={handleDismiss(paneId)}
                        showCloseButton={showCloseButton}
                        headerClassName={headerClassName}
                        titleClassName={titleClassName}
                        bodyClassName={bodyClassName}
                    >
                        {children}
                    </DrawerPane>
                )
            )}
        </>
    );
}, 'AppPaneGroup');
